/**
 * Current Players Slice for Zustand Store
 * Manages real-time player count data for games
 */

import type IGamesCurrentPlayers from '../../types/models/IGamesCurrentPlayers';
import { getGamesCurrentPlayersUrl } from '@repo/common';

export interface CurrentPlayersSlice {
  // State
  currentPlayers: IGamesCurrentPlayers;
  currentPlayersLoading: boolean;
  currentPlayersError: string | null;
  
  // Actions
  fetchCurrentPlayers: (apiHost: string) => Promise<void>;
  getPlayerCountForGame: (gameKey: string) => number;
  setPlayerCountForGame: (gameKey: string, count: number) => void;
}

export const createCurrentPlayersSlice = (set: any, get: any) => ({
  // Initial state
  currentPlayers: {},
  currentPlayersLoading: false,
  currentPlayersError: null,
  
  // Actions
  fetchCurrentPlayers: async (apiHost: string) => {
    set({ currentPlayersLoading: true, currentPlayersError: null });
    
    try {
      const url = getGamesCurrentPlayersUrl(apiHost);
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json() as IGamesCurrentPlayers;
        set({ 
          currentPlayers: data, 
          currentPlayersLoading: false,
          currentPlayersError: null 
        });
      } else {
        throw new Error(`Request failed with ${response.status} status code.`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch current players data';
      console.error('Failed to fetch current players data:', err);
      set({ 
        currentPlayersError: errorMessage,
        currentPlayersLoading: false 
      });
    }
  },
  
  getPlayerCountForGame: (gameKey: string) => {
    const { currentPlayers } = get();
    return currentPlayers[gameKey] || 0;
  },

  setPlayerCountForGame: (gameKey: string, count: number) => {
    // Immutable update so subscribers (usePlayerCountForGame → badge) fire.
    const current = get().currentPlayers as IGamesCurrentPlayers;
    set({ currentPlayers: { ...current, [gameKey]: count } });
  }
});
